import React, { ChangeEvent, FocusEvent, FC, useEffect, useState } from 'react'
import axios, { AxiosResponse } from 'axios'
import {
  SButton,
  SButtonContainer,
  SCloseButton,
  SCloseIcon,
  SContainer,
  SForm,
  SInputLabel,
  SLabelText,
  SOverlay,
  SSubmitButton,
  STextArea,
  STextAreaLabel,
  STitle,
  SInput,
  SCaptchaContainer,
  SCaptchaImage,
  SFakeCaptchaImageBlock,
  SImage,
} from '@components/LeavePopup/styled'
import { ICaptchaData, ICaptchaDataResponse, IFieldsData, IProps, ISubmitResponse } from '@components/LeavePopup/types'
import {
  captchaDataDefault,
  fieldsDataInit,
  validate,
  api,
  ApiResponseStatuses,
  ErrorMessages,
} from '@components/LeavePopup/constants'
import { STypographyDefault, STypographyDesc } from '@components/Typography'
import { colors } from '@styles/theme'
import ManImage from '@images/manInFrontOfComputer.svg'
import { Box } from '../../../../ipp-pr/src/components'

const LeavePopup: FC<IProps> = ({ onClose }) => {
  const [fieldsData, setFieldsData] = useState<IFieldsData>(fieldsDataInit)
  const [captchaData, setCaptchaData] = useState<ICaptchaData>(captchaDataDefault)
  const [wasSent, setWasSent] = useState(false)

  useEffect(() => {
    document.body.style.overflow = 'hidden'

    axios
      .get(api.captcha)
      .then(({ data: { value, src } }: AxiosResponse<ICaptchaDataResponse>) => {
        setCaptchaData({
          ...captchaData,
          code: value,
          imageURL: src,
        })
      })
      .catch((err) => console.log(err))

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const data = new FormData()

    Object.keys(fieldsData).forEach((field) => data.append(field, fieldsData[field as keyof IFieldsData].data.trim()))

    data.append('captcha_word', captchaData.inputFieldValue)
    data.append('captcha_code', captchaData.code)

    axios
      .post(api.submit, data)
      .then(({ data: { status, message, src, value } }: AxiosResponse<ISubmitResponse>) => {
        if (status === ApiResponseStatuses.success) {
          setWasSent(true)
        } else if (status === ApiResponseStatuses.error && message === ErrorMessages.captchaFalse) {
          setCaptchaData({
            ...captchaDataDefault,
            code: value,
            imageURL: src,
          })
        } else {
          console.log(message)
        }
      })
      .catch((err) => console.log(err))
  }

  const onChange = ({ target: { name, value } }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFieldsData({
      ...fieldsData,
      [name]: {
        data: !value.trim() ? '' : value,
        isValid: validate[name](value.trim()),
      },
    })
  }

  const onBlur = ({ target: { name, value } }: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFieldsData({
      ...fieldsData,
      [name]: {
        ...fieldsData[name as keyof IFieldsData],
        isValid: validate[name](value.trim()),
      },
    })
  }

  const captchaChange = ({ target: { value } }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCaptchaData({
      ...captchaData,
      inputFieldValue: value,
    })
  }

  const areAllFieldsValid = () =>
    !!(
      fieldsData.name.data &&
      fieldsData.name.isValid &&
      fieldsData.email.data &&
      fieldsData.email.isValid &&
      fieldsData.question.data &&
      fieldsData.question.isValid
    )

  return (
    <SOverlay>
      <SContainer>
        {wasSent ? (
          <>
            <STitle>{fieldsData.name.data.trim()}</STitle>
            <STypographyDefault fontSize="16px" lineHeight="24px" mt="12px">
              Спасибо за проявленный интерес к программе. Мы с Вами свяжемся в течение 24 часов (в рабочие дни).
            </STypographyDefault>
            <SImage src={ManImage} alt="Мужчина перед компьютером" />
            <SButton onClick={onClose} black={1}>
              Хорошо
            </SButton>
            <SCloseButton type="button" onClick={onClose}>
              <SCloseIcon />
            </SCloseButton>
          </>
        ) : (
          <>
            <STitle>Остались вопросы и нужна консультация?</STitle>
            <SForm onSubmit={onSubmit} noValidate>
              <SInputLabel>
                <SLabelText>Имя</SLabelText>
                <SInput
                  name="name"
                  value={fieldsData.name.data}
                  onChange={onChange}
                  onBlur={onBlur}
                  type="text"
                  placeholder="Иванов Иван Иванович"
                  required
                  error={!fieldsData.name.isValid}
                />
              </SInputLabel>
              <Box height="16px" mt="4px" mb="12px">
                {!fieldsData.name.isValid && (
                  <STypographyDesc as="p" color={colors.red}>
                    Имя должно состоять из кириллицы
                  </STypographyDesc>
                )}
              </Box>

              <SInputLabel>
                <SLabelText>E-mail</SLabelText>
                <SInput
                  name="email"
                  value={fieldsData.email.data}
                  onChange={onChange}
                  onBlur={onBlur}
                  type="text"
                  placeholder="Name@company.ru"
                  required
                  error={!fieldsData.email.isValid}
                />
              </SInputLabel>
              <Box height="16px" mt="4px" mb="12px">
                {!fieldsData.email.isValid && (
                  <STypographyDesc as="p" color={colors.red}>
                    Введите корректный email
                  </STypographyDesc>
                )}
              </Box>

              <STextAreaLabel>
                <SLabelText>Ваш вопрос</SLabelText>
                <STextArea
                  name="question"
                  value={fieldsData.question.data}
                  onChange={onChange}
                  onBlur={onBlur}
                  required
                  error={!fieldsData.question.isValid}
                />
              </STextAreaLabel>
              <Box height="16px" mt="4px" mb="24px">
                {!fieldsData.question.isValid && (
                  <STypographyDesc as="p" color={colors.red}>
                    Вопрос должен состоять из кириллицы
                  </STypographyDesc>
                )}
              </Box>

              <SCaptchaContainer>
                {captchaData.imageURL ? (
                  <SCaptchaImage src={captchaData.imageURL} alt="CAPTCHA" />
                ) : (
                  <SFakeCaptchaImageBlock />
                )}
                <SInput
                  name="captcha"
                  value={captchaData.inputFieldValue}
                  type="text"
                  onChange={captchaChange}
                  placeholder="Введите код с картинки"
                  required
                />
              </SCaptchaContainer>

              <SButtonContainer>
                <SSubmitButton type="submit" disabled={!areAllFieldsValid()}>
                  Задать вопрос
                </SSubmitButton>
                <SButton onClick={onClose}>Нет, спасибо</SButton>
              </SButtonContainer>
            </SForm>
            <SCloseButton type="button" onClick={onClose}>
              <SCloseIcon />
            </SCloseButton>
          </>
        )}
      </SContainer>
    </SOverlay>
  )
}

export default LeavePopup
